// The owners' frame: a sidebar on a desktop (Home, Garage with each car, Telemetry, Library, Create; Help and the
// account at its foot), narrowed to a rail of icons if the person likes; a tab bar on a phone or a tablet. The page
// goes beside or above them, with its own toolbar (Page.tsx).
import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { useAuth } from '../auth';
import { useAccount, useDevices, useVehicles } from '../queries';
import { Avatar, cx, Dot, IconButton } from '../ui';
import { GaugeIcon, QuestionIcon, SidebarSimpleIcon } from '../ui/icons';
import s from './AppFrame.module.css';
import { isHere, OWNER_PLACES } from './nav';

const RAIL_KEY = 'tune.sidebar';
const WEEK_MS = 7 * 86_400_000;

/** The sidebar, wide or a rail of icons: the person's choice, remembered in this browser. */
export function useRail(): [boolean, () => void] {
  const [rail, setRail] = useState(() => {
    try {
      return localStorage.getItem(RAIL_KEY) === 'rail';
    } catch {
      return false;
    }
  });
  const toggle = () =>
    setRail((was) => {
      try {
        localStorage.setItem(RAIL_KEY, was ? 'full' : 'rail');
      } catch {
        // Not remembered: it's the person's choice for now.
      }
      return !was;
    });
  return [rail, toggle];
}

function Sidebar({ rail, toggle }: { rail: boolean; toggle: () => void }) {
  const { pathname } = useLocation();
  const auth = useAuth();
  const session = auth.session('owners');
  const account = useAccount(session !== null);
  const vehicles = useVehicles();
  const devices = useDevices();
  const cars = vehicles.data?.vehicles ?? [];
  const seen = (carId: string) =>
    (devices.data?.devices ?? []).some(
      (d) => d.vehicleId === carId && d.lastSeenAt !== null && Date.now() - Date.parse(d.lastSeenAt) < WEEK_MS,
    );
  const name = account.data?.displayName ?? session?.name ?? session?.email ?? '';
  const email = session?.email ?? '';
  return (
    <nav className={s.sidebar} aria-label="TUNE Analytics">
      <div className={s.sideHead}>
        <Link to="/" className={s.brand} aria-label="TUNE Analytics: Home">
          <img src="/logo.avif" alt="" width={38} height={21} />
          <span>TUNE Analytics</span>
        </Link>
        <IconButton
          label={rail ? 'Widen the sidebar' : 'Narrow the sidebar'}
          icon={<SidebarSimpleIcon size={20} />}
          onClick={toggle}
        />
      </div>
      <ul className={s.places} role="list">
        {OWNER_PLACES.map((place) => {
          const here = isHere(place, pathname);
          const Icon = place.icon;
          return (
            <li key={place.to}>
              <Link
                to={place.to}
                className={cx(s.place, here && s.here)}
                aria-current={pathname === place.to ? 'page' : undefined}
                title={rail ? place.label : undefined}
              >
                <Icon size={20} weight={here ? 'fill' : 'bold'} aria-hidden="true" />
                <span className={s.placeLabel}>{place.label}</span>
              </Link>
              {place.to === '/garage' && cars.length > 0 && (
                <ul className={s.cars} role="list" aria-label="Your cars">
                  {cars.map((car) => {
                    const to = `/garage/${car.id}`;
                    const on = pathname === to || pathname.startsWith(`${to}/`);
                    return (
                      <li key={car.id}>
                        <Link
                          to={to}
                          className={cx(s.car, on && s.here)}
                          aria-current={pathname === to ? 'page' : undefined}
                        >
                          <Dot tone={seen(car.id) ? 'good' : 'neutral'} />
                          <span>
                            {car.nickname}
                            {seen(car.id) && <span className="visually-hidden">, its TUNE MFD seen this week</span>}
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      <div className={s.sideFoot}>
        <Link
          to="/help"
          className={cx(s.place, isHere({ to: '/help', label: '', icon: QuestionIcon }, pathname) && s.here)}
          title={rail ? 'Help' : undefined}
        >
          <QuestionIcon size={20} weight="bold" aria-hidden="true" />
          <span className={s.placeLabel}>Help</span>
        </Link>
        {account.data?.role === 'admin' && (
          <Link to="/admin" className={s.place} title={rail ? 'Admin dashboard' : undefined}>
            <GaugeIcon size={20} weight="bold" aria-hidden="true" />
            <span className={s.placeLabel}>Admin dashboard</span>
          </Link>
        )}
        <Link
          to="/account"
          className={s.account}
          aria-current={pathname === '/account' ? 'page' : undefined}
          aria-label={rail ? `Account: ${name}` : undefined}
          title={rail ? 'Account' : undefined}
        >
          <Avatar name={account.data?.displayName ?? session?.name} email={email} />
          <span className={s.who}>
            <span className={s.whoName}>{name}</span>
            <span className={s.whoEmail}>{email}</span>
          </span>
        </Link>
      </div>
    </nav>
  );
}

function TabBar() {
  const { pathname } = useLocation();
  return (
    <nav className={s.tabbar} aria-label="TUNE Analytics">
      {OWNER_PLACES.map((place) => {
        const here = isHere(place, pathname);
        const Icon = place.icon;
        return (
          <Link
            key={place.to}
            to={place.to}
            className={cx(s.tab, here && s.here)}
            aria-current={pathname === place.to ? 'page' : undefined}
          >
            <Icon size={26} weight={here ? 'fill' : 'regular'} aria-hidden="true" />
            <span>{place.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

/** The owners' frame around a page. `immersive` pages (the Analyser, the config creator) have no tab bar on a phone. */
export function AppFrame({ immersive = false, children }: { immersive?: boolean; children: ReactNode }) {
  const [rail, toggle] = useRail();
  return (
    <div className={cx(s.frame, rail && s.rail, immersive && s.immersive)}>
      <a href="#main" className={s.skip}>
        Skip to the page
      </a>
      <Sidebar rail={rail} toggle={toggle} />
      <main id="main" tabIndex={-1} className={s.main}>
        {children}
      </main>
      <TabBar />
    </div>
  );
}
